/**
 * aiTutorFlag — the ONE cross-app read/write path for `lwa_ai_tutor_v1`, the
 * parent-consented "AI Tutor" flag (live, conversational cloud-AI tutoring for
 * the child app). Phase 2 COPPA consent surface.
 *
 * ⚠️ SERVER IS AUTHORITATIVE. This signed client flag exists ONLY to drive
 * INSTANT optimistic UI on the parent Settings surfaces — it is NOT the consent
 * record and NEVER substitutes for it. The COPPA verifiable-parental-consent
 * record lives on account-api (`/families/{parentId}/ai-settings`), and
 * tutor-api gates EVERY paid model call on that server record (fail-closed). A
 * parent who flips this flag with no server write has NOT consented; the paid
 * path stays off. Settings reads the server state on mount and reflects it.
 *
 * WHY here in ui-shared: the parent portal (web-app) and the mobile shell both
 * toggle this flag but CANNOT import web-child (`@child`) at runtime, where the
 * signed-envelope writer (`flagGuard.setRigorFlag`) lives. So the reader AND a
 * parent-gated writer live here, over the shared `createSignedFlag` primitive
 * (signedFlag.ts) that replicates flagGuard.ts's scheme byte-for-byte — so an
 * envelope written here is accepted by flagGuard's `readGuardedFlag` (and
 * vice-versa), and the ops hook `window.__lwaRigorFlags.set('lwa_ai_tutor_v1', …)`
 * stays interoperable. `lwa_ai_tutor_v1` is registered in
 * web-child/flagGuard.ts GUARDED_FLAGS + DEFAULT_OFF_FLAGS (default OFF) too.
 *
 * DEFAULT OFF: an absent key reads false — shipping this changes nothing until a
 * parent signs it ON. A bare / legacy / foreign localStorage write ('on', '1',
 * 'true', …) is IGNORED; only a correctly PARENT-signed `{ v:'on' }` envelope
 * flips it on, so a child (or a stray script) can never enable it. Never throws.
 */

import { createSignedFlag } from './signedFlag';

export const AI_TUTOR_FLAG = 'lwa_ai_tutor_v1';

const flag = createSignedFlag(AI_TUTOR_FLAG); // DEFAULT OFF

/**
 * True when a PARENT is signed in on this device: a structurally valid,
 * unexpired `auth_token` JWT (the parent-portal session — the same identity
 * flagGuard.hasParentGate rides on). The child token (`child_auth_token`) does
 * NOT qualify.
 */
export function hasAiTutorParentGate(): boolean {
  return flag.hasParentGate();
}

/**
 * Read the AI Tutor client flag (optimistic UI only). Default OFF. Returns true
 * ONLY for a correctly parent-signed `{ v:'on', sig }` envelope; every other
 * shape (absent, bare 'on'/'1'/'true', malformed JSON, wrong/missing signature)
 * reads false. Never throws. NOT the consent record — the server is.
 */
export function isAiTutorEnabled(): boolean {
  return flag.isEnabled();
}

/**
 * Parent-gated write of the optimistic client flag. Turning it OFF (back to the
 * default) clears the key; turning it ON stores a signed `{ v:'on', sig }`
 * envelope. Refuses (returns false, writes nothing) without a live parent
 * session — so a child-side or stray write can never enable the feature. This
 * is a UI mirror ONLY; the caller must ALSO write the server consent record.
 */
export function setAiTutorEnabled(enabled: boolean): boolean {
  return flag.setEnabled(enabled);
}

/** TEST-ONLY seam: sign the flag ON without the parent-JWT check, so unit tests
 *  can exercise the enabled paths (mirrors flagGuard's __setGuardedFlagOnForTests). */
export function __setAiTutorOnForTests(): void {
  flag.__setOnForTests();
}
